import { useQuery } from '@apollo/client';
import { GET_SLACK_DESTINATION_ENTITIES } from '../../utils/apollo/slack/queries';
import {
  IGetSlackDestinationEntitiesQuery,
  IGetSlackDestinationEntitiesQueryVariables,
  ISlackDestinationEntitiesFieldsFragment,
  ISlackDestinationFieldsFragment,
} from '../../utils/apollo/graphqlSchema';
import { useDebounce } from '../../utils/hooks';
import React, { useCallback, useMemo, useState } from 'react';
import { ReactSelectMenu } from '../Dropdown/StyledReactSelect';
import {
  components,
  components as ReactSelectComponents,
  MenuProps,
  Options,
} from 'react-select';
import { SingleValueProps } from 'react-select/dist/declarations/src/components/SingleValue';
import { OptionProps } from 'react-select/dist/declarations/src/components/Option';
import {
  BasicDropdownIndicator,
  BasicSingleValue,
  ControlWithSearchIcon,
} from '../Dropdown/StyledReactSelect/SelectUtils';
import { UnreachableCaseError } from 'ts-essentials';
import { ISlackDestinationOption } from './types';
import { isNil } from 'lodash';
import { MaybeNil } from '../../utils/interfaces';
import { assertNotNil } from '../../utils/types';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { getSlackDestinationOptionOrNull } from './utils';
import SlackDestinationLabel from './SlackDestinationLabel';

const CHANNEL_LIMIT = 100;

const TeamName = styled.div`
  padding: 6px 8px;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
  color: ${theme.colors.secondary900};
`;

const SlackDestinationValue = (
  props: SingleValueProps<ISlackDestinationOption, false>,
) => {
  return (
    <BasicSingleValue {...props}>
      <SlackDestinationLabel value={props.data} />
    </BasicSingleValue>
  );
};

const SlackDestinationOption = (
  props: OptionProps<ISlackDestinationOption, false>,
) => {
  return (
    <components.Option {...props}>
      <SlackDestinationLabel value={props.data} />
    </components.Option>
  );
};

// filtering is performed server-side
const noopFilterOption = () => true;

interface IProps {
  value: MaybeNil<ISlackDestinationFieldsFragment>;
  onChange?: (value: MaybeNil<ISlackDestinationFieldsFragment>) => void;
  disabled?: boolean;
}

const SlackDestinationDropdown = ({ value, onChange, disabled }: IProps) => {
  const [term, setTerm] = useState('');
  const debouncedTerm = useDebounce(term, 300);

  const [destinationEntities, setDestinationEntities] =
    useState<ISlackDestinationEntitiesFieldsFragment | null>(null);

  const { loading } = useQuery<
    IGetSlackDestinationEntitiesQuery,
    IGetSlackDestinationEntitiesQueryVariables
  >(GET_SLACK_DESTINATION_ENTITIES, {
    variables: {
      term: debouncedTerm,
      channelLimit: CHANNEL_LIMIT,
    },
    onCompleted: ({ slackDestinationEntities }) =>
      setDestinationEntities(slackDestinationEntities),
    skip: disabled,
  });

  const options = useMemo<ISlackDestinationOption[]>(() => {
    if (!isNil(destinationEntities)) {
      return [...destinationEntities.members, ...destinationEntities.channels];
    } else {
      return [];
    }
  }, [destinationEntities]);

  // selected value may not be among the `options` (e.g. different team (Slack Workspace))
  const selectedValue = getSlackDestinationOptionOrNull(value);

  const isOptionSelected = useCallback(
    (
      option: ISlackDestinationOption,
      selectValues: Options<ISlackDestinationOption>,
    ) => {
      if (
        !isNil(value) &&
        !isNil(destinationEntities) &&
        value.team.id !== destinationEntities.team.id
      ) {
        // the selected value is from different team (Slack Workspace),
        // never selected
        return false;
      } else {
        return selectValues.some((selectValue) => {
          if (selectValue.__typename !== option.__typename) {
            return false;
          } else {
            return selectValue.id === option.id;
          }
        });
      }
    },
    [destinationEntities, value],
  );

  const handleChange = (newValue: MaybeNil<ISlackDestinationOption>) => {
    let changeValue: ISlackDestinationFieldsFragment | null;
    if (isNil(newValue)) {
      changeValue = null;
    } else {
      const team = destinationEntities?.team;
      assertNotNil(team);
      if (newValue.__typename === 'SlackChannel') {
        changeValue = {
          __typename: 'SlackChannelDestination',
          team,
          channel: newValue,
        };
      } else if (newValue.__typename === 'SlackMember') {
        changeValue = {
          __typename: 'SlackMemberDestination',
          team,
          member: newValue,
        };
      } else {
        throw new UnreachableCaseError(newValue);
      }
    }
    setTerm('');
    onChange?.(changeValue);
  };

  const SlackMenu = useCallback(
    ({ children, ...rest }: MenuProps<ISlackDestinationOption, false>) => {
      const teamName = destinationEntities?.team.name;

      return (
        <ReactSelectComponents.Menu {...rest}>
          {!isNil(teamName) && <TeamName>{teamName}</TeamName>}
          {children}
        </ReactSelectComponents.Menu>
      );
    },
    [destinationEntities?.team.name],
  );

  return (
    <ReactSelectMenu
      isDisabled={disabled}
      options={options}
      onChange={handleChange}
      value={selectedValue}
      isMulti={false}
      isSearchable={true}
      isClearable={true}
      isLoading={loading}
      inputValue={term}
      onInputChange={setTerm}
      placeholder="Search for a channel or select yourself as the recipient."
      blurInputOnSelect={true}
      isOptionSelected={isOptionSelected}
      filterOption={noopFilterOption}
      components={{
        Menu: SlackMenu,
        Option: SlackDestinationOption,
        SingleValue: SlackDestinationValue,
        DropdownIndicator: (props) => BasicDropdownIndicator({ props }),
        LoadingIndicator: ReactSelectComponents.LoadingIndicator,
        Control: ControlWithSearchIcon,
      }}
    />
  );
};

export default SlackDestinationDropdown;
